import React, { useState, useEffect } from 'react';
import { useMiniERPEDI } from './hooks/useMiniERPEDI';
import { useStatusBanner, StatusBanner } from './components/MiniERPStatusBanner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faHandshake, faCheckCircle, faTimesCircle, faHourglassHalf } from '@fortawesome/free-solid-svg-icons';

const STATUS_META = {
  Pending: { icon: faHourglassHalf, color: 'text-amber-400' },
  Kept: { icon: faCheckCircle, color: 'text-emerald-400' },
  Broken: { icon: faTimesCircle, color: 'text-rose-400' },
};

const MiniERPTrasPromiseToPay = ({ caseId }) => {
  const { createTrasPTP, fetchTrasPTPs } = useMiniERPEDI();
  const { status, showSuccess, showError } = useStatusBanner();
  const [ptps, setPtps] = useState([]);
  const [form, setForm] = useState({ promiseDate: '', promisedAmount: '', capturedVia: 'Phone Call', notes: '' });

  const load = async () => {
    const res = await fetchTrasPTPs(caseId);
    setPtps(res?.data || []);
  };

  useEffect(() => { if (caseId) load(); }, [caseId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.promiseDate || !form.promisedAmount) {
      showError('Promise date and amount are required.');
      return;
    }
    try {
      await createTrasPTP(caseId, { ...form, promisedAmount: Number(form.promisedAmount) });
      showSuccess('Promise to pay logged.');
      setForm({ promiseDate: '', promisedAmount: '', capturedVia: 'Phone Call', notes: '' });
      await load();
    } catch (err) {
      showError(err.message);
    }
  };

  return (
    <div className="p-4">
      <h2 className="text-lg font-bold text-cyan-200 mb-4 flex items-center gap-2">
        <FontAwesomeIcon icon={faHandshake} /> Promise to Pay
      </h2>

      <form onSubmit={handleSubmit} className="bg-gray-800 rounded-2xl p-4 space-y-3 mb-5">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-gray-400 text-xs block mb-1">Promise Date</label>
            <input type="date" value={form.promiseDate} onChange={(e) => setForm({ ...form, promiseDate: e.target.value })}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm" required />
          </div>
          <div>
            <label className="text-gray-400 text-xs block mb-1">Promised Amount (₹)</label>
            <input type="number" value={form.promisedAmount} onChange={(e) => setForm({ ...form, promisedAmount: e.target.value })}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm" required />
          </div>
        </div>
        <div>
          <label className="text-gray-400 text-xs block mb-1">Captured Via</label>
          <select value={form.capturedVia} onChange={(e) => setForm({ ...form, capturedVia: e.target.value })}
            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-2 py-2 text-sm">
            <option>Phone Call</option>
            <option>WhatsApp</option>
            <option>In-Person Visit</option>
            <option>Email</option>
          </select>
        </div>
        <textarea placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
          style={{ height: '70px' }} className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm" />
        <StatusBanner status={status} />
        <button type="submit" className="w-full bg-cyan-600 hover:bg-cyan-500 rounded-xl py-2.5 font-medium flex items-center justify-center gap-2">
          <FontAwesomeIcon icon={faPlus} /> Log Promise
        </button>
      </form>

      <h3 className="text-sm font-semibold text-gray-300 mb-2">History</h3>
      <div className="space-y-2">
        {ptps.length === 0 && <p className="text-gray-500 text-sm">No promises logged yet for this case.</p>}
        {ptps.map((p) => {
          const meta = STATUS_META[p.status] || STATUS_META.Pending;
          return (
            <div key={p._id} className="bg-gray-800 border border-gray-700 rounded-xl px-3 py-2 flex justify-between items-center text-sm">
              <div>
                <div className="text-white font-medium">₹{p.promisedAmount} by {new Date(p.promiseDate).toLocaleDateString()}</div>
                <div className="text-gray-500 text-xs">{p.capturedVia}{p.notes ? ` • ${p.notes}` : ''}</div>
              </div>
              <span className={`flex items-center gap-1 text-xs ${meta.color}`}>
                <FontAwesomeIcon icon={meta.icon} /> {p.status}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MiniERPTrasPromiseToPay;
